import React, { FC } from "react";

import "../styles.scss";
import "../scripts";

import {
  primaryBlock,
  PrimaryBlockConfig,
  getDefaultProps,
  KameleonElement,
  SecondaryBlockSize,
  TertiaryBlockSize,
  BlockTheme,
  renderSecondaryBlock,
  renderTertiaryBlock,
  BlockType,
} from "@kameleon-core/lib";
import { Box } from "theme-ui";

import { webflowHOC } from "@kameleon-common/webflow-js";
import { Variants } from "@kameleon-util/global-theme";
import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";
import { MediaCollectionType, layoutOptions } from "./types/prop-types";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import getEvents from "./js/webflow";

/** MediaCollection Primary */
const blockConfig: PrimaryBlockConfig = {
  secondaryBlockSize: SecondaryBlockSize.Medium,
  tertiaryBlockSize: TertiaryBlockSize.Default,
  blockType: BlockType.Primary,
  blockTheme: BlockTheme.light,
};

const MediaCollectionComponent: FC<MediaCollectionType> = ({
  SecondaryBlock,
  TertiaryBlock,
  tagline,
  header,
  description,
  topImages,
  bottomImages,
  layoutProps,
}: MediaCollectionType) => {
  const mediaIsTop = layoutProps.mediaPlacement === "top";
  return (
    <Box className="primary-media-collection-two-col-animated-rows-v1 section_layout414">
      <Box className="padding-global">
        <Box className="container-large padding-section-large layout414_component">
          <Box
            data-w-id="c312f05d-6016-23ad-061e-08a66a5c3384"
            className={`layout414_content-bottom ${
              !mediaIsTop && "margin-xxlarge"
            }`}
            sx={{ order: mediaIsTop ? -1 : 1 }}
          >
            <Box className="layout414_image-list image-list-top">
              {topImages.map((image, i) => (
                <Box key={`${image}-${i}`} className="layout414_image-wrapper">
                  <Variants.Image
                    src={image.url}
                    loading="lazy"
                    alt={image.alt}
                    className="layout414_image"
                  />
                </Box>
              ))}
            </Box>
            <Box className="layout414_image-list image-list-bottom">
              {bottomImages.map((image, i) => (
                <Box key={`${image}-${i}`} className="layout414_image-wrapper">
                  <Variants.Image
                    src={image.url}
                    loading="lazy"
                    alt={image.alt}
                    className="layout414_image"
                  />
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            className={`margin-top layout414_content-top ${
              mediaIsTop && "margin-xxlarge"
            }`}
          >
            <Box
              sx={{
                textAlign:
                  layoutProps.headerAlignment === "left" ? "left" : "center",
                order: `${layoutProps.headerPlacement === "left" ? "-1" : "1"}`,
              }}
              className="layout414_content-left margin-bottom"
            >
              {tagline && (
                <Box className="margin-xsmall margin-bottom">
                  <Variants.TagLine>{tagline}</Variants.TagLine>
                </Box>
              )}
              {header && <Variants.Heading>{header}</Variants.Heading>}
            </Box>
            <Box className="layout414_content-right">
              {description && (
                <Variants.ParagraphMedium>
                  {description}
                </Variants.ParagraphMedium>
              )}
              {renderSecondaryBlock(SecondaryBlock)}
              <Box className="tertiary-block">
                {renderTertiaryBlock(TertiaryBlock)}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ComponentWithWebflow = webflowHOC(MediaCollectionComponent, [
  {
    module: "ix2",
    getEvents: (pageId: any): any => getEvents(pageId),
  },
]);

export const MediaCollection = primaryBlock<MediaCollectionType>(
  blockConfig,
  ComponentWithWebflow
);
MediaCollection.defaultProps = getDefaultProps<MediaCollectionType>(defaults);

export const Element: KameleonElement = {
  component: MediaCollection,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
